/**
 * Garment Order detail — document-style view + status-gated actions
 * (ADR-0039, ADR-0043), following the V1.1 document-detail grammar (sticky
 * header with back square + mono number chip + StatusPill, letterhead
 * document card, sticky action rail + summary card).
 *
 * A buyer PO covers many GARMENTS (ADR-0043): the document renders one
 * section per garment, each with its own colour × size grid and its own
 * Design studio / Link style file actions — engineering picks garments up
 * one at a time, and the order only reaches `linked` once every garment has
 * a Style File.
 *
 *   - open     → Accept order · per-garment Design studio · Edit · Mark closed · Cancel
 *   - accepted → per-garment Design studio / Link style file · Mark closed · Cancel
 *   - linked   → Mark closed · Cancel
 *   - terminal (closed / cancelled) → no actions
 *
 * Accepting is the seller accepting the buyer's offer (ADR-0042) — it is what
 * unlocks tech pack generation, which spends an AI credit. Authoring in the
 * studio stays free, so Design studio is reachable per garment while the
 * order is open or accepted.
 *
 * Append-only: accept/close/cancel are status moves, never deletes; edits are
 * allowed while open or accepted (server-enforced via
 * GarmentOrderNotEditableError) — the buyer's PO paperwork often lands after
 * acceptance.
 *
 * @see apps/web/src/features/purchase-orders/components/po-detail-page.tsx —
 *      the document-detail pattern this mirrors
 * @see prototype/purchases/purchase-order-detail.html — V1.1 baseline
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, Ban, Check, FileText, Link2, Loader2, Pencil } from "lucide-react";
import { Alert, Button, ConfirmDialog, StatusPill, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import { orderStatusLabel, orderStatusTone, type GarmentOrderStatus } from "../lib/order-status.js";
import { dateRelative } from "../lib/relative-date.js";
import { LinkStyleDialog, type LinkStyleDialogOrder } from "./link-style-dialog.js";

interface PivotedColour {
  colour: string;
  colourHex: string | null;
  /** size → qty. A size the colour never ordered is ABSENT, not 0. */
  cells: Record<string, number>;
  total: number;
}

interface PivotedGrid {
  colours: PivotedColour[];
  sizes: string[];
  sizeTotals: Record<string, number>;
}

/**
 * Flat sparse cells → a colour × size chart. Colours and sizes keep
 * first-seen (position) order so the grid renders stably, matching the
 * Style File's ratio chart.
 */
function pivotGrid(
  lines: ReadonlyArray<{ colour: string; colourHex: string | null; size: string; qty: number }>,
): PivotedGrid {
  const colours: PivotedColour[] = [];
  const sizes: string[] = [];
  const sizeTotals: Record<string, number> = {};

  for (const line of lines) {
    let row = colours.find((c) => c.colour === line.colour);
    if (!row) {
      row = { colour: line.colour, colourHex: line.colourHex, cells: {}, total: 0 };
      colours.push(row);
    }
    if (!sizes.includes(line.size)) sizes.push(line.size);
    row.cells[line.size] = (row.cells[line.size] ?? 0) + line.qty;
    row.total += line.qty;
    sizeTotals[line.size] = (sizeTotals[line.size] ?? 0) + line.qty;
  }
  return { colours, sizes, sizeTotals };
}

export function GarmentOrderDetailPage({ orderId }: { orderId: string }) {
  const utils = trpc.useUtils();
  const detail = trpc.garmentOrders.get.useQuery({ id: orderId });

  const [linkTarget, setLinkTarget] = useState<LinkStyleDialogOrder | null>(null);
  const [confirmAction, setConfirmAction] = useState<"accepted" | "closed" | "cancelled" | null>(
    null,
  );

  const transition = trpc.garmentOrders.transitionStatus.useMutation({
    onSuccess: async (result) => {
      await Promise.all([
        utils.garmentOrders.get.invalidate({ id: orderId }),
        utils.garmentOrders.list.invalidate(),
      ]);
      toast.success(
        result.status === "accepted"
          ? "Order accepted"
          : result.status === "closed"
            ? "Order closed"
            : "Order cancelled",
        {
          description:
            result.status === "accepted"
              ? "Tech pack generation is unlocked for every garment on this order."
              : "The order's status has been updated. The record stays in the book.",
        },
      );
      setConfirmAction(null);
    },
    onError: (err) => {
      toast.error("Couldn't update the order", { description: err.message });
      setConfirmAction(null);
    },
  });

  const createStyle = trpc.styles.createFromOrderItem.useMutation({
    onSuccess: async (result) => {
      await utils.garmentOrders.get.invalidate({ id: orderId });
      toast.success(`Style File created — job ${result.jobNo}`, {
        description: "Open it from the linked style ref to start the design.",
      });
    },
    onError: (err) => toast.error("Couldn't create the style file", { description: err.message }),
  });

  if (detail.isLoading) {
    return <p className="p-6 text-sm text-muted">Loading order…</p>;
  }
  if (detail.error || !detail.data) {
    return (
      <div className="p-6">
        <Alert
          intent="bad"
          title="Couldn't load the order"
          description={detail.error?.message ?? "Order not found."}
        />
      </div>
    );
  }

  const order = detail.data;
  const status = order.status as GarmentOrderStatus;
  const busy = transition.isPending;
  const rel = dateRelative(order.deliveryDate);
  const buyerLocation = [order.buyerCity, order.buyerState].filter(Boolean).join(", ");
  const openLinkDialog = (item: { id: string; name: string }) =>
    setLinkTarget({ itemId: item.id, orderNo: order.orderNo, name: item.name });
  const linkedItemCount = order.items.filter((it) => it.styleId).length;

  return (
    <div className="flex min-h-full flex-col bg-bg">
      {/* Head */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
        <Link
          to="/merchandising/orders"
          aria-label="Back to orders"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
            Order
            <span className="rounded-full bg-raised px-2 py-0.5 font-mono text-xs font-medium text-muted">
              {order.orderNo}
            </span>
            <StatusPill tone={orderStatusTone(status)} size="sm" dot>
              {orderStatusLabel(status)}
            </StatusPill>
          </h1>
          <p className="mt-0.5 text-xs text-muted">
            <strong className="text-ink-2">{order.buyerName}</strong> · Logged{" "}
            {formatDate(order.createdAt)}
            {order.deliveryDate ? ` · delivery ${formatDate(order.deliveryDate)}` : ""}
          </p>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-[1200px] flex-1 gap-5 px-6 pb-16 pt-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* Document */}
        <article className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
          <div className="border-b border-line bg-raised px-5 py-2.5 text-xs text-muted">
            <strong className="text-ink">{order.orderNo}</strong> · {order.buyerName} ·{" "}
            {formatDate(order.createdAt)}
          </div>
          <div className="p-6">
            {/* Letterhead marker */}
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                  Garment order
                </p>
                <h2 className="mt-1 font-mono text-xl font-bold text-ink">{order.orderNo}</h2>
                {order.description ? (
                  <p className="mt-1 text-sm text-ink-2">{order.description}</p>
                ) : null}
              </div>
              <dl className="text-right text-sm">
                <div className="flex justify-end gap-2">
                  <dt className="text-muted">Logged</dt>
                  <dd className="text-ink-2">{formatDate(order.createdAt)}</dd>
                </div>
                {order.deliveryDate ? (
                  <div className="mt-1 flex justify-end gap-2">
                    <dt className="text-muted">Delivery by</dt>
                    <dd className="text-ink-2">
                      {formatDate(order.deliveryDate)}
                      {rel ? <span className={`ml-1.5 text-xs ${rel.className}`}>{rel.label}</span> : null}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>

            {/* Buyer */}
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted">Buyer</p>
              <p className="mt-1 text-base font-bold text-ink">{order.buyerName}</p>
              {buyerLocation ? <p className="text-sm text-muted">{buyerLocation}</p> : null}
              {order.poNumber ? (
                <p className="mt-1 text-xs text-muted">
                  Buyer PO {order.poNumber}
                  {order.poDate ? ` · ${formatDate(order.poDate)}` : ""}
                </p>
              ) : null}
              {order.buyerRef ? (
                <p className="mt-1 text-xs text-muted">Buyer ref {order.buyerRef}</p>
              ) : null}
            </div>

            {/* One section per garment (ADR-0043) */}
            <div className="mt-6 flex flex-col gap-6">
              {order.items.map((item, itemIndex) => {
                const grid = pivotGrid(item.lines);
                return (
                  <div key={item.id} className="border-t border-line pt-5 first:border-t-0 first:pt-0">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted">
                          Garment {order.items.length > 1 ? itemIndex + 1 : ""}
                        </p>
                        <p className="mt-1 text-sm font-medium text-ink">{item.name}</p>
                        {item.spec ? (
                          <p className="mt-1 max-w-xl text-xs text-muted">{item.spec}</p>
                        ) : null}
                      </div>
                      <div className="text-right text-xs">
                        {item.styleId && item.styleRef ? (
                          <Link
                            to="/merchandising/styles/$styleId/overview"
                            params={{ styleId: item.styleId }}
                            className="font-mono font-medium text-sky-700 hover:underline"
                          >
                            {item.styleRef}
                          </Link>
                        ) : (
                          <span className="text-muted">Style file not linked</span>
                        )}
                      </div>
                    </div>

                    {/* Colour × size grid (ADR-0041) — sparse, so an unordered
                        cell renders as "—", not 0. */}
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full border-collapse border border-line text-sm">
                        <thead>
                          <tr className="text-xs uppercase tracking-wide text-muted">
                            <th className="w-40 border border-line px-3 py-2 text-left font-medium">Colour</th>
                            {grid.sizes.map((size) => (
                              <th key={size} className="border border-line px-3 py-2 text-right font-semibold text-ink">
                                {size}
                              </th>
                            ))}
                            <th className="border border-line px-3 py-2 text-right font-semibold text-ink">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {grid.colours.map((row) => (
                            <tr key={row.colour}>
                              <td className="border border-line px-3 py-2.5">
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    aria-hidden
                                    className="h-3 w-3 shrink-0 rounded-full border border-line"
                                    style={{ backgroundColor: row.colourHex ?? "transparent" }}
                                  />
                                  <span className="text-ink-2">{row.colour || "Not recorded"}</span>
                                </span>
                              </td>
                              {grid.sizes.map((size) => {
                                const n = row.cells[size];
                                return (
                                  <td key={size} className="border border-line px-3 py-2.5 text-right font-mono tabular-nums text-ink-2">
                                    {n === undefined ? (
                                      <span className="text-line-2">—</span>
                                    ) : (
                                      n.toLocaleString("en-IN")
                                    )}
                                  </td>
                                );
                              })}
                              <td className="border border-line px-3 py-2.5 text-right font-mono font-bold tabular-nums text-ink">
                                {row.total.toLocaleString("en-IN")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td className="border border-line px-3 py-2.5 text-xs uppercase tracking-wide text-muted">
                              Qty (pcs)
                            </td>
                            {grid.sizes.map((size) => (
                              <td key={size} className="border border-line px-3 py-2.5 text-right font-mono tabular-nums text-ink-2">
                                {(grid.sizeTotals[size] ?? 0).toLocaleString("en-IN")}
                              </td>
                            ))}
                            <td className="border border-line px-3 py-2.5 text-right font-mono font-bold tabular-nums text-ink">
                              {item.totalQty.toLocaleString("en-IN")}
                            </td>
                          </tr>
                          <tr>
                            <td className="border border-line px-3 py-2.5 text-xs uppercase tracking-wide text-muted">
                              Share
                            </td>
                            {grid.sizes.map((size) => {
                              const n = grid.sizeTotals[size] ?? 0;
                              return (
                                <td key={size} className="border border-line px-3 py-2.5 text-right font-mono tabular-nums text-muted">
                                  {item.totalQty > 0 ? `${Math.round((n / item.totalQty) * 100)}%` : "—"}
                                </td>
                              );
                            })}
                            <td className="border border-line px-3 py-2.5 text-right font-mono tabular-nums text-muted">
                              {item.totalQty > 0 ? "100%" : "—"}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {(status === "open" || status === "accepted") && !item.styleId ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          disabled={createStyle.isPending && createStyle.variables?.itemId === item.id}
                          onClick={() => createStyle.mutate({ itemId: item.id })}
                        >
                          {createStyle.isPending && createStyle.variables?.itemId === item.id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <FileText size={13} />
                          )}
                          Create style
                        </Button>
                        <Button
                          size="sm"
                          intent="ghost"
                          className="border border-line bg-paper"
                          onClick={() => openLinkDialog(item)}
                        >
                          <Link2 size={13} />
                          Link style file
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {order.notes ? (
              <div className="mt-5 rounded-md border border-line bg-raised p-3 text-sm text-ink-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted">Notes</p>
                <p className="mt-1 whitespace-pre-line">{order.notes}</p>
              </div>
            ) : null}
          </div>
        </article>

        {/* Action rail */}
        <aside className="w-full">
          <div className="sticky top-20 space-y-4">
            <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
              <h4 className="text-sm font-semibold text-ink">Actions</h4>
              <div className="mt-3 flex flex-col gap-2">
                {/* Acceptance is the seller accepting the buyer's offer (ADR-0042).
                    It unlocks tech pack generation, which spends an AI credit. */}
                {status === "open" ? (
                  <Button
                    disabled={busy}
                    className="justify-start"
                    onClick={() => setConfirmAction("accepted")}
                  >
                    <Check size={15} />
                    Accept order
                  </Button>
                ) : null}

                {status === "open" ? (
                  <Button asChild intent="ghost" className="justify-start border border-line bg-paper">
                    <Link to="/merchandising/orders/$orderId/edit" params={{ orderId: order.id }}>
                      <Pencil size={15} />
                      Edit
                    </Link>
                  </Button>
                ) : null}

                {status === "open" || status === "accepted" || status === "linked" ? (
                  <>
                    <Button
                      intent={status === "linked" ? "primary" : "ghost"}
                      disabled={busy}
                      className={
                        status === "linked"
                          ? "justify-start"
                          : "justify-start border border-line bg-paper"
                      }
                      onClick={() => setConfirmAction("closed")}
                    >
                      <Check size={15} />
                      Mark closed
                    </Button>
                    <Button
                      intent="ghost"
                      disabled={busy}
                      className="justify-start text-bad hover:bg-bad-50"
                      onClick={() => setConfirmAction("cancelled")}
                    >
                      <Ban size={15} />
                      Cancel order
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-muted">
                    This order is {orderStatusLabel(status).toLowerCase()} — no further actions.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-md border border-line bg-paper p-3.5 shadow-sm">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted">Summary</h4>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Status</dt>
                  <dd>
                    <StatusPill tone={orderStatusTone(status)} size="sm">
                      {orderStatusLabel(status)}
                    </StatusPill>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Total pieces</dt>
                  <dd className="font-mono font-semibold tabular-nums text-ink">
                    {order.totalQty.toLocaleString("en-IN")}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Garments</dt>
                  <dd className="font-mono tabular-nums text-ink-2">{order.items.length}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Delivery</dt>
                  <dd className="text-ink-2">
                    {order.deliveryDate ? formatDate(order.deliveryDate) : "—"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Style files linked</dt>
                  <dd className="font-mono tabular-nums text-ink-2">
                    {linkedItemCount} / {order.items.length}
                  </dd>
                </div>
              </dl>
              {linkedItemCount < order.items.length && (status === "open" || status === "accepted") ? (
                <p className="mt-3 text-xs leading-relaxed text-muted">
                  Engineering picks each garment up — link its Style File when opened. The order
                  reaches "Style linked" once every garment has one.
                </p>
              ) : null}
            </div>
          </div>
        </aside>
      </main>

      <LinkStyleDialog order={linkTarget} onClose={() => setLinkTarget(null)} />

      <ConfirmDialog
        open={confirmAction !== null}
        onOpenChange={(open) => (!open ? setConfirmAction(null) : undefined)}
        title={
          confirmAction === "accepted"
            ? "Accept this order?"
            : confirmAction === "cancelled"
              ? "Cancel this order?"
              : "Close this order?"
        }
        description={
          confirmAction === "accepted"
            ? `${order.orderNo} will be accepted — the workspace commits to it, and tech pack generation unlocks for every garment (each generation spends an AI credit). You can still correct the order afterwards.`
            : confirmAction === "cancelled"
              ? `${order.orderNo} will be marked cancelled. The record stays in the book (append-only) — it can't be reopened.`
              : `${order.orderNo} will be marked closed as fulfilled. It can't be reopened.`
        }
        confirmLabel={
          confirmAction === "accepted"
            ? "Accept order"
            : confirmAction === "cancelled"
              ? "Cancel order"
              : "Mark closed"
        }
        intent={confirmAction === "cancelled" ? "danger" : "default"}
        pending={busy}
        onConfirm={() => {
          if (confirmAction) transition.mutate({ id: order.id, status: confirmAction });
        }}
      />
    </div>
  );
}
